import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Sparkles, 
  Coffee, 
  Target, 
  Users, 
  MapPin, 
  Clock, 
  Heart, 
  Zap,
  Search,
  PlusCircle,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface IntentChip {
  id: string;
  label: string;
  type: 'mood' | 'action' | 'location' | 'time' | 'social' | 'content';
  icon?: React.ComponentType<any>;
  color?: string;
  query?: string; // The search query this chip represents
}

interface IntentChipsProps {
  activeIntents: IntentChip[];
  suggestedIntents: IntentChip[];
  onAddIntent: (intent: IntentChip) => void;
  onRemoveIntent: (intentId: string) => void;
  onIntentClick: (intent: IntentChip) => void;
  className?: string;
}

const SUGGESTED_INTENTS: IntentChip[] = [
  {
    id: 'motivation',
    label: 'Need motivation',
    type: 'mood',
    icon: Zap,
    color: 'bg-mood-motivation-muted text-mood-motivation border-mood-motivation/30',
    query: "I'm not motivated today"
  },
  {
    id: 'inspiration',
    label: 'Seeking inspiration',
    type: 'mood',
    icon: Sparkles,
    color: 'bg-mood-inspiration-muted text-mood-inspiration border-mood-inspiration/30',
    query: "Show me something inspiring"
  },
  {
    id: 'discovery',
    label: 'Discover new things',
    type: 'action',
    icon: Search,
    color: 'bg-mood-discovery-muted text-mood-discovery border-mood-discovery/30',
    query: "Help me discover something new"
  },
  {
    id: 'local-artists',
    label: 'Local artists',
    type: 'location',
    icon: MapPin,
    color: 'bg-mood-focus-muted text-mood-focus border-mood-focus/30',
    query: "Find artists in my area"
  },
  {
    id: 'focus-help',
    label: 'Help me focus',
    type: 'action',
    icon: Target,
    color: 'bg-mood-focus-muted text-mood-focus border-mood-focus/30',
    query: "Help me focus"
  },
  {
    id: 'creative-break',
    label: 'Creative break',
    type: 'time',
    icon: Coffee,
    color: 'bg-mood-relaxation-muted text-mood-relaxation border-mood-relaxation/30',
    query: "I need a creative break"
  },
  {
    id: 'community',
    label: 'Find community',
    type: 'social',
    icon: Users,
    color: 'bg-mood-connection-muted text-mood-connection border-mood-connection/30',
    query: "Help me find my community"
  },
  {
    id: 'beautiful',
    label: 'Something beautiful',
    type: 'content',
    icon: Heart,
    color: 'bg-mood-connection-muted text-mood-connection border-mood-connection/30',
    query: "Show me something beautiful"
  }
];

export const IntentChips: React.FC<IntentChipsProps> = ({
  activeIntents,
  suggestedIntents,
  onAddIntent,
  onRemoveIntent,
  onIntentClick,
  className
}) => {
  // Combine custom suggested intents with default ones
  const allSuggested = [...suggestedIntents, ...SUGGESTED_INTENTS];
  
  // Filter out already active intents from suggestions
  const availableSuggestions = allSuggested.filter(
    suggestion => !activeIntents.some(active => active.id === suggestion.id)
  );

  const renderChip = (intent: IntentChip, isActive: boolean = false) => {
    const Icon = intent.icon;
    
    return (
      <div
        key={intent.id}
        className={cn(
          "group flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all duration-200 cursor-pointer",
          isActive 
            ? cn(
                "border-primary/50 shadow-sm",
                intent.color || "bg-primary/10 text-primary border-primary/20"
              )
            : cn(
                "border-border/50 hover:border-border bg-background/50 hover:bg-muted/50",
                intent.color || "text-muted-foreground hover:text-foreground"
              )
        )}
        onClick={() => onIntentClick(intent)}
      >
        {Icon && <Icon className="h-3 w-3 flex-shrink-0" />}
        <span className="text-xs font-medium whitespace-nowrap">
          {intent.label}
        </span>
        
        {isActive ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveIntent(intent.id);
            }}
            className="ml-1 p-0.5 rounded-full hover:bg-destructive/20 hover:text-destructive transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddIntent(intent);
            }}
            className="ml-1 p-0.5 rounded-full hover:bg-primary/20 hover:text-primary transition-colors opacity-0 group-hover:opacity-100"
          >
            <PlusCircle className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Active Intent Chips */}
      {activeIntents.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground px-1">
            Active Context
          </div>
          <div className="flex flex-wrap gap-1.5">
            {activeIntents.map(intent => renderChip(intent, true))}
          </div>
        </div>
      )}

      {/* Suggested Intent Chips */}
      {availableSuggestions.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground px-1">
            Quick Context
          </div>
          <div className="flex flex-wrap gap-1.5">
            {availableSuggestions.slice(0, 8).map(intent => renderChip(intent, false))}
          </div>
        </div>
      )}

      {/* No active intents message */}
      {activeIntents.length === 0 && availableSuggestions.length === 0 && (
        <div className="text-center py-4 text-muted-foreground">
          <div className="text-sm">No context selected</div>
          <div className="text-xs mt-1">Try typing your mood or intent above</div>
        </div>
      )}
    </div>
  );
};